import { Alert } from '@neo/primitives/alert'
// Import from `/pure`: the default `@stripe/stripe-js` entry eagerly injects
// js.stripe.com (and its fraud telemetry) on import, which would load for every
// billing-page visitor even with the embedded flag off. `/pure` defers all loading
// until `loadStripe()` is actually called (i.e. only when this component mounts).
import { loadStripe } from '@stripe/stripe-js/pure'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { PRIVATE_FIELD_CLASS } from 'v2/configs/constants'
import { queryClient } from 'v2/configs/rq-setup'
import { billingService } from 'v2/shared/services/billing-service'
import { billingQueryKeys } from 'v2/shared/utils/query-key-management'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { logger } from 'v2/utils/logger'
import { errorToast, successToast } from 'v2/utils/toasts'
import { PaymentMethod } from '../billing.config'
import { getCardCheckoutErrorToast } from '../card-checkout-error'
import { getAccountId, trackPaymentFailed } from '../utils'

interface StripeEmbeddedCheckoutProps {
  onClose: () => void
}

// PSP-hosted (PCI-safe) Stripe add-card. The raw PAN is entered inside Stripe's
// embedded iframe and never touches our UI or servers. Flow:
//   1. POST card/checkout-session -> { client_secret, publishable_key, checkout_session_id }
//   2. loadStripe(publishable_key).initEmbeddedCheckout({ clientSecret }) + mount()
//   3. onComplete -> POST card/checkout-complete({ checkout_session_id }) -> card_mapping_id
// Cards that need a redirect step (e.g. 3DS) skip onComplete — the backend creates the
// session with redirect_on_completion=if_required, so Stripe sends them to the return_url
// (/payment/callback?session_id=...), where PaymentCallback runs the same checkout-complete.
// Both the in-page and redirect paths therefore persist the card.
// If checkout-complete fails we never show the old optimistic "will appear
// shortly" toast: a terminal 4xx rejection (e.g. an India-issued card on a USD
// account) is rejected identically by the webhook and won't appear, so we show
// the backend's reason; a 5xx/network failure is unconfirmed, so we ask the user
// to check back. See getCardCheckoutErrorToast in ../card-checkout-error.
export const StripeEmbeddedCheckout = ({ onClose }: StripeEmbeddedCheckoutProps) => {
  const accountId = getAccountId()
  const mountRef = useRef<HTMLDivElement>(null)
  const checkoutRef = useRef<{ mount: (el: HTMLElement) => void; destroy: () => void } | null>(null)
  // Keep the latest onClose without re-running init (which would create a new
  // checkout session on every parent re-render).
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const completeCheckout = async (checkoutSessionId: string) => {
      try {
        const result = await billingService.completeCardCheckout(
          { checkout_session_id: checkoutSessionId },
          accountId,
        )
        track(AnalyticsEvents.CARD_ADDED, {
          method_type: PaymentMethod.CARD,
          country: result.card_country,
        })
        successToast({ title: 'Success', description: 'Card added successfully' })
      } catch (err) {
        logger.error('StripeEmbeddedCheckout: completeCardCheckout failed', { err })
        errorToast(getCardCheckoutErrorToast(err))
        trackPaymentFailed('add_card', err, { gateway: 'stripe' })
      } finally {
        queryClient.invalidateQueries({ queryKey: billingQueryKeys().cards(accountId) })
        onCloseRef.current()
      }
    }

    const init = async () => {
      try {
        const session = await billingService.createCardCheckoutSession(
          // Redirect-required cards (3DS) come back to /payment/callback, which runs
          // checkout-complete; non-redirect cards finish in-page via onComplete below.
          { return_url: `${window.location.origin}/payment/callback` },
          accountId,
        )
        if (cancelled) return
        if (!session.publishable_key) {
          // Backend has no Stripe config for this account's product/currency.
          setError('Card payments are not configured for this account. Please contact support.')
          setIsLoading(false)
          return
        }
        const stripe = await loadStripe(session.publishable_key)
        if (cancelled) return
        if (!stripe) {
          setError('Unable to load the secure card form. Please try again.')
          setIsLoading(false)
          return
        }
        const checkout = await stripe.initEmbeddedCheckout({
          clientSecret: session.client_secret,
          onComplete: () => completeCheckout(session.checkout_session_id),
        })
        if (cancelled) {
          checkout.destroy()
          return
        }
        checkoutRef.current = checkout
        if (mountRef.current) {
          checkout.mount(mountRef.current)
        }
        setIsLoading(false)
      } catch (err) {
        logger.error('StripeEmbeddedCheckout: failed to initialise embedded checkout', { err })
        if (!cancelled) {
          setError('Unable to load the secure card form. Please try again.')
          setIsLoading(false)
          trackPaymentFailed('add_card', err, { gateway: 'stripe' })
        }
      }
    }

    init()

    return () => {
      cancelled = true
      checkoutRef.current?.destroy()
      checkoutRef.current = null
    }
  }, [accountId])

  if (error) {
    return (
      <div data-testid="billing-embedded-checkout-error">
        <Alert variant="destructive" title={error} />
      </div>
    )
  }

  return (
    // Card entry (Stripe iframe) — excluded from both session recorders (defense-in-depth)
    <div className={`${PRIVATE_FIELD_CLASS} flex flex-col gap-3`} data-private>
      {isLoading && (
        <div
          className="flex items-center justify-center py-10"
          data-testid="billing-embedded-checkout-loading"
        >
          <Loader2 className="size-6 animate-spin text-secondary" />
        </div>
      )}
      <div ref={mountRef} className="min-h-[320px]" data-testid="billing-embedded-checkout-mount" />
    </div>
  )
}
